/*
TODO:
This code can be replace using the library https://www.npmjs.com/package/dagre
and the ReactFlowProvider

Example: https://reactflow.dev/examples/layouting/
*/

import { ROOT_NODE_ID } from "src/config/constants";

// BlockY is the space between blocks in the Y axis
const BlockY = 300;
// BlockX is the space between blocks in the X axis
const BlockX = 400;

const INF = 1000000000;

interface Position {
  x: number;
  y: number;
}

const enqueue = (queue: string[], item: string) => queue.push(item);
const dequeue = (queue: string[]) => queue.shift();

// TODO: use an external library to abstract this code
// bfs is the breadth-first search algorithm, used to obtain the depth and parent of each node.
const bfs = (
  root: string,
  adjacency: Map<string, string[]>
): [Map<string, number>, Map<string, string[]>] => {
  let depths: Map<string, number> = new Map();
  let parent: Map<string, string[]> = new Map();

  let queue: string[] = [];
  enqueue(queue, root);

  depths.set(root, 0)
  parent.set("-1", [root])

  while (queue.length > 0) {
    const source = queue[0];
    dequeue(queue);
    adjacency.get(source)?.forEach((target) => {
      if (depths.has(target)) {
        return
      }
      const sourceDepth = depths.get(source)? depths.get(source) : 0
      depths.set(target, sourceDepth as number + 1);

      const newChildren = parent.get(source) || []
      newChildren.push(target)
      parent.set(source, newChildren)

      enqueue(queue, target);
    });
  }
  return [depths, parent];
};

// TODO: this idea by itself will never work when there are loops.
// To support recursive connections we need to be able to
// mark an edge as a recursive edge.

// dfs is the depth-first search algorithm
const dfs = (
  source: string,
  removeNodeId: string,
  adjacency: Map<string, string[]>,
  visited: Map<string, boolean>,
  newAdjacency: Map<string, string[]>): void => {
  if (!visited) {
    visited = new Map()
  }

  visited.set(source, true);
  const targets = adjacency.get(source)
  if (!targets) {
    if (source !== removeNodeId) {
      // TODO: find another way to keep the leaf nodes in the adjacency.
      newAdjacency.set(source, ['leaf'])
    }
    return
  }

  if (!newAdjacency.get(source)) {
    newAdjacency.set(source, [])
  }

  for (let index = 0; index < targets.length; index ++ ) {
    const target = targets[index]
    if (target === removeNodeId || visited.has(target)) {
      continue;
    }

    const newTargets = newAdjacency.get(source) || []
    newTargets?.push(target)
    if (newTargets) {
      newAdjacency.set(source, newTargets)
    }

    dfs(target, removeNodeId, adjacency, visited, newAdjacency)
  }

  return
}

// generateAdjacency is used to obtain an adjacency list from a Flow, used to represent a finite graph.
const generateAdjacency = (flow: Flow): Map<string, string[]> => {
  const adjacency = new Map<string, string[]>();

  flow.edges.forEach((edge) => {
    const from = edge.source,
      to = edge.target;
    const currentTargets = adjacency.get(from);
    if (!currentTargets) {
      adjacency.set(from, [to]);
      return;
    }

    currentTargets?.push(to);
    adjacency.set(from, currentTargets as string[]);
  });

  return adjacency;
};

const getInitialYPosition = (
  nodesLength: number,
  parents: Map<string, string[]>
): number => {
  // @ts-ignore
  // Subtract 1 to not count the root's parent.
  const parentsQuantity = [...parents.keys()].length - 1
  const leafs = nodesLength - parentsQuantity;
  // BlockY is the approximate height of the blocks in the canvas.
  // Subtract because the upper Y plane in the canvas it's negative.
  return BlockY - (((leafs - 1) * BlockY) / 2);
};

// TODO: this idea by itself will never work when there are loops.
// To support recursive connections we need to be able to
// mark an edge as a recursive edge.

// dfsGetXYPositions is the depth-first search algorithm
const dfsGetXYPositions = (
  source: string,
  countedLeafs: number,
  adjacency: Map<string, string[]>,
  visited: Map<string, boolean>,
  initialY: number,
  depths: Map<string, number>,
  positions: Map<string, Position>): {
    countedLeafs: number,
    yPosition: number
  } => {
  if (!visited) {
    visited = new Map()
  }
  if (!positions) {
    positions = new Map()
  }

  visited.set(source, true);
  const targets = adjacency.get(source)
  if (!targets) {
    const currentLeafXPosition = BlockX * (depths.get(source) || 0)
    const currentLeafYPosition = initialY + (BlockY * countedLeafs)

    countedLeafs += 1
    positions.set(source, {
      x: currentLeafXPosition,
      y: currentLeafYPosition,
    })

    return {
      countedLeafs: 1,
      yPosition: currentLeafYPosition
    }
  }

  let minTargetsPosition = INF, maxTargetsPosition = -INF
  let leafCounter = 0

  for (let index = 0; index < targets.length; index ++ ) {
    const target = targets[index]
    if (visited.has(target)) {
      continue;
    }
    const { yPosition: targetPosition, countedLeafs: leafCounts } = dfsGetXYPositions(target, leafCounter + countedLeafs, adjacency, visited, initialY, depths, positions)
    leafCounter += leafCounts
    minTargetsPosition = Math.min(minTargetsPosition, targetPosition)
    maxTargetsPosition = Math.max(maxTargetsPosition, targetPosition)
  }

  const currentNodeXPosition = BlockX * (depths.get(source) || 0)
  const currentNodeYPosition = (minTargetsPosition + maxTargetsPosition) / 2;
  positions.set(source, {
    x: currentNodeXPosition,
    y: currentNodeYPosition,
  })

  return {
    countedLeafs: leafCounter,
    yPosition: currentNodeYPosition
  }
}

const getPositions = (flow: Flow): Map<string, Position> => {
  const adjacency = generateAdjacency(flow);
  const [depths, parent] = bfs(ROOT_NODE_ID, adjacency);

  const initialY = getInitialYPosition(flow.nodes.length, parent);

  const visited: Map<string, boolean> = new Map()
  const positions: Map<string, Position> = new Map()
  dfsGetXYPositions(ROOT_NODE_ID, 0, adjacency, visited, initialY, depths, positions);

  return positions;
};

const buildFlowFromAdjacency = (adjacency: Map<string, string[]>, flow: Flow): Flow => {
  const newFlow: Flow = {
    inboundMessage: flow.inboundMessage,
    isDeployed: flow.isDeployed,
    errorMessages: flow.errorMessages,
    variables: flow.variables,
    nodes: [],
    edges: []
  }

  newFlow.nodes = flow.nodes.filter(node => adjacency.has(node.id))

  newFlow.edges = flow.edges.filter(edge => {
    const newTargets = adjacency.get(edge.source)
    if (!newTargets) {
      return false
    }
    if (!newTargets.includes(edge.target)) {
      return false
    }

    return true
  })

  return newFlow
}

const shiftMultipleOptionsOrder = (flow: Flow, multipleBlockId: string, removedNodeOrder: number) => {
  const targets = flow.edges.map((edge) => {
    if (edge.source !== multipleBlockId) {
      return
    }

    return edge.target
  })
  if (!targets) {
    return
  }

  for(let i = 0; i < flow.nodes.length; i ++){
    if (!targets.includes(flow.nodes[i].id)) {
      continue
    }

    if (flow.nodes[i].data?.order && (flow.nodes[i].data?.order || 0) < removedNodeOrder) {
      continue
    }

    // TODO: can prevOrder be undefined?
    const prevOrder = flow.nodes[i].data?.order as number
    flow.nodes[i].data.order = prevOrder - 1
  }
}

export const OrganizeFlow = (flow: Flow): Flow => {
  const positions: Map<string, Position> = getPositions(flow);

  const newNodes = flow.nodes.map((node) => {
    const position = positions.get(node.id)
    if (!position) {
      return node;
    }

    // Dimensions are included to calculate position because
    // the origin of the node component is not in the center of the element.
    node.position = {
      x: position.x - node.data?.dimensions.width / 2,
      y: position.y - node.data?.dimensions.height / 2,
    };

    // Allows to place the root element closer to the first block.
    if (node.id === ROOT_NODE_ID) {
      node.position.x += BlockX / 3
    }

    return node;
  });
  flow.nodes = newNodes;

  return flow;
};

export const RemoveNode = (flow: Flow, nodeId: string): Flow => {
  const adjacency = generateAdjacency(flow);

  const visited: Map<string, boolean> = new Map()
  const newAdjacency: Map<string, string[]> = new Map()
  dfs(ROOT_NODE_ID, nodeId, adjacency, visited, newAdjacency)

  const newFlow = buildFlowFromAdjacency(newAdjacency, flow)

  // shift Multiple Options Order when a child of a multiple options block is removed
  const removedNodeParentId = flow.edges.find((edge) => edge.target === nodeId)?.source
  const removedNodeParent = flow.nodes.find((node) => node.id === removedNodeParentId)
  const removedNode = flow.nodes.find((node) => node.id === nodeId)
  if (removedNode && removedNode?.data?.order && removedNodeParentId && removedNodeParent && removedNodeParent.type === 'multiple') {
    shiftMultipleOptionsOrder(newFlow, removedNodeParentId, removedNode?.data?.order)
  }

  return newFlow
};
